import React, { useMemo } from 'react';
import { FaCalendarAlt, FaLeaf, FaTimes, FaUtensils } from 'react-icons/fa';
import { formatPlanPriceWhole } from './planPricingUtils';
import { WEEKLY_MENU_INDICATIVE_NOTE } from './weeklyMenuNotes';

const DAY_ABBR = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

const TABLE_ROWS = 4;

function MealCell({ meal, slotPrice, planName, slotLabel, useOrganic, onMealClick }) {
  if (!meal) {
    return <td className="mp-slot-menu-popover__meal mp-slot-menu-popover__meal--empty" aria-hidden="true" />;
  }

  const content = (
    <>
      <span className="mp-slot-menu-popover__dish">{meal.item}</span>
      {meal.portion ? <span className="mp-slot-menu-popover__portion">{meal.portion}</span> : null}
    </>
  );

  if (!onMealClick) {
    return (
      <td className="mp-slot-menu-popover__meal">
        <div className="mp-slot-menu-popover__meal-inner mp-slot-menu-popover__meal-inner--static">
          {content}
        </div>
      </td>
    );
  }

  return (
    <td className="mp-slot-menu-popover__meal">
      <button
        type="button"
        className="mp-slot-menu-popover__meal-inner"
        onClick={() => onMealClick({
          name: meal.item,
          price: slotPrice,
          category: planName,
          slotLabel,
          availability: meal.day,
          portion: meal.portion,
          calories: meal.calories,
          isOrganic: useOrganic,
        })}
      >
        {content}
      </button>
    </td>
  );
}

export default function SlotWeeklyMenuPopover({
  planName,
  slotLabel,
  slotPrice,
  meals = [],
  useOrganic = false,
  onClose,
  onMealClick,
}) {
  const { leftCol, rightCol } = useMemo(() => {
    const left = meals.slice(0, TABLE_ROWS);
    const right = meals.slice(TABLE_ROWS, TABLE_ROWS + 3);
    return { leftCol: left, rightCol: right };
  }, [meals]);

  if (!slotLabel) return null;

  const theme = useOrganic ? 'organic' : 'regular';

  return (
    <div
      className="mp-slot-menu-popover"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className={`mp-slot-menu-popover__card mp-slot-menu-popover__card--${theme}`}
        role="dialog"
        aria-labelledby="mp-slot-menu-popover-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mp-slot-menu-popover__accent" aria-hidden />

        <header className="mp-slot-menu-popover__header">
          <div className="mp-slot-menu-popover__header-text">
            <span className="mp-slot-menu-popover__eyebrow">
              <FaCalendarAlt aria-hidden />
              Sample weekly menu
            </span>
            <h3 id="mp-slot-menu-popover-title">{slotLabel}</h3>
            <div className="mp-slot-menu-popover__chips">
              {planName ? <span className="mp-slot-menu-popover__chip">{planName}</span> : null}
              {slotPrice != null ? (
                <span className="mp-slot-menu-popover__chip mp-slot-menu-popover__chip--price">
                  {formatPlanPriceWhole(slotPrice)}
                  <small>/day</small>
                </span>
              ) : null}
              <span className={`mp-slot-menu-popover__chip mp-slot-menu-popover__chip--type mp-slot-menu-popover__chip--${theme}`}>
                {useOrganic ? <FaLeaf aria-hidden /> : <FaUtensils aria-hidden />}
                {useOrganic ? 'Organic' : 'Regular'}
              </span>
            </div>
          </div>
          <button type="button" className="mp-slot-menu-popover__close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </header>

        <div className="mp-slot-menu-popover__body">
          {meals.length ? (
            <div className="mp-slot-menu-popover__table-wrap">
              <table className="mp-slot-menu-popover__table">
                <colgroup>
                  <col className="mp-slot-menu-popover__col-day" />
                  <col className="mp-slot-menu-popover__col-meal" />
                  <col className="mp-slot-menu-popover__col-gap" />
                  <col className="mp-slot-menu-popover__col-day" />
                  <col className="mp-slot-menu-popover__col-meal" />
                </colgroup>
                <tbody>
                  {Array.from({ length: TABLE_ROWS }, (_, rowIndex) => {
                    const left = leftCol[rowIndex];
                    const right = rightCol[rowIndex];
                    return (
                      <tr key={rowIndex}>
                        <th scope="row" className="mp-slot-menu-popover__day">
                          {left ? DAY_ABBR[left.day] || left.day.slice(0, 3) : ''}
                        </th>
                        <MealCell
                          meal={left}
                          slotPrice={slotPrice}
                          planName={planName}
                          slotLabel={slotLabel}
                          useOrganic={useOrganic}
                          onMealClick={onMealClick}
                        />
                        <td className="mp-slot-menu-popover__divider" aria-hidden="true" />
                        <th scope="row" className="mp-slot-menu-popover__day">
                          {right ? DAY_ABBR[right.day] || right.day.slice(0, 3) : ''}
                        </th>
                        <MealCell
                          meal={right}
                          slotPrice={slotPrice}
                          planName={planName}
                          slotLabel={slotLabel}
                          useOrganic={useOrganic}
                          onMealClick={onMealClick}
                        />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mp-slot-menu-popover__empty">Menu not available for this slot.</p>
          )}
        </div>

        {meals.length ? (
          <footer className="mp-slot-menu-popover__footer">
            <p className="mp-slot-menu-popover__indicative-note" role="note">
              <strong>Please note:</strong> {WEEKLY_MENU_INDICATIVE_NOTE}
            </p>
            <div className="mp-slot-menu-popover__footer-meta">
              <span>Mon – Sun</span>
              <span>Tap a dish for details</span>
            </div>
          </footer>
        ) : null}
      </div>
    </div>
  );
}
